import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Determines whether a cart mutation needs a completed document request so a
 * Cart Transform app can reapply its bundle presentation.
 *
 * @param {Object} state
 * @param {boolean} state.isPricedCartTransformBundle
 * @param {boolean} state.isFastBundleCartLine
 * @param {number} state.currentQuantity
 * @param {number} state.nextQuantity
 * @param {number} state.pricedBundleCountAfterMutation
 * @returns {boolean}
 */
export function shouldReconcileCartTransformMutation(state) {
  if (!state.isPricedCartTransformBundle) return false;
  if (
    !Number.isFinite(state.currentQuantity) ||
    !Number.isFinite(state.nextQuantity) ||
    state.nextQuantity < 0 ||
    state.nextQuantity === state.currentQuantity
  ) return false;

  if (state.isFastBundleCartLine) {
    return state.nextQuantity > 0 || state.pricedBundleCountAfterMutation > 0;
  }

  return state.nextQuantity < state.currentQuantity
    && state.pricedBundleCountAfterMutation > 0;
}

/**
 * Detects a Fast Bundle line whose provider allocation is not yet available.
 * Ordinary Cart Transform lines and already-priced bundles are ignored.
 *
 * @param {Array<{isFastBundleCartLine: boolean, isPricedCartTransformBundle: boolean}>} rows
 * @returns {boolean}
 */
export function shouldRecoverUnpricedFastBundlePricing(rows) {
  return rows.some(
    (row) => row.isFastBundleCartLine && !row.isPricedCartTransformBundle
  );
}

/**
 * Counts settled transform rows while allowing Fast Bundle recovery to ignore
 * unrelated transforms that happen to be priced in the same cart.
 *
 * @param {ArrayLike<HTMLElement>} rows
 * @param {{fastBundleOnly?: boolean}} [options]
 * @returns {number}
 */
export function countPricedCartTransformRows(rows, { fastBundleOnly = false } = {}) {
  return Array.from(rows || []).filter((row) => (
    row.dataset.cartTransformPriced === 'true' &&
    (!fastBundleOnly || row.dataset.fastBundleCartLine === 'true')
  )).length;
}

/**
 * Verifies a completed cart render. Fast Bundle mutations are tied to the
 * provider bundle ID, quantity and exact provider price; unrelated transforms
 * cannot satisfy the check.
 *
 * @param {ArrayLike<HTMLElement>} rows
 * @param {Object} expectation
 * @param {number} expectation.expectedPricedBundleCount
 * @param {boolean} [expectation.fastBundleOnly]
 * @param {string} [expectation.targetFastBundleId]
 * @param {number|null} [expectation.expectedQuantity]
 * @param {number|null} [expectation.expectedProviderLinePriceCents]
 * @param {number|null} [expectation.expectedDisplayedItemCount]
 * @param {number|null} [expectation.renderedDisplayedItemCount]
 * @param {string[]} [expectation.requiredFastBundleIds]
 * @returns {boolean}
 */
export function isCartTransformProbeSettled(rows, expectation) {
  const renderedRows = Array.from(rows || []);
  const {
    expectedPricedBundleCount,
    fastBundleOnly = false,
    targetFastBundleId = '',
    expectedQuantity = null,
    expectedProviderLinePriceCents = null,
    expectedDisplayedItemCount = null,
    renderedDisplayedItemCount = null,
    requiredFastBundleIds = [],
  } = expectation;
  const pricedBundleCount = countPricedCartTransformRows(renderedRows, { fastBundleOnly });

  if (pricedBundleCount < expectedPricedBundleCount) return false;
  if (
    Number.isSafeInteger(expectedDisplayedItemCount)
    && renderedDisplayedItemCount !== expectedDisplayedItemCount
  ) return false;
  for (const bundleId of requiredFastBundleIds) {
    const matchingRows = renderedRows.filter(row => (
      row.dataset.fastBundleCartLine === 'true'
      && row.dataset.fastBundleId === bundleId
    ));
    if (matchingRows.length !== 1) return false;
    const providerLinePriceCents = Number.parseInt(
      matchingRows[0].dataset.fastBundleProviderLinePriceCents ?? '',
      10
    );
    if (
      matchingRows[0].dataset.cartTransformPriced !== 'true'
      || !Number.isSafeInteger(providerLinePriceCents)
      || providerLinePriceCents < 0
    ) return false;
  }
  if (!targetFastBundleId) return true;

  const targetRows = renderedRows.filter(row => (
    row.dataset.fastBundleCartLine === 'true'
    && row.dataset.fastBundleId === targetFastBundleId
  ));
  if (expectedQuantity === 0) return targetRows.length === 0;
  if (targetRows.length !== 1 || !Number.isSafeInteger(expectedQuantity) || expectedQuantity < 1) {
    return false;
  }

  const targetRow = targetRows[0];
  const renderedQuantity = Number.parseInt(targetRow.dataset.cartLineQuantity ?? '', 10);
  const providerLinePriceCents = Number.parseInt(
    targetRow.dataset.fastBundleProviderLinePriceCents ?? '',
    10
  );
  return targetRow.dataset.cartTransformPriced === 'true'
    && renderedQuantity === expectedQuantity
    && Number.isSafeInteger(expectedProviderLinePriceCents)
    && providerLinePriceCents === expectedProviderLinePriceCents;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #isRecoveringCartTransformPricing = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.#recoverUnpricedFastBundlePricing();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartItemRowByLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getCartItemRowByLine(line);
    const paletteBuilderParentKey = cartItemRowToRemove?.dataset.paletteBuilderParentKey;

    if (paletteBuilderParentKey) {
      this.removePaletteBuilderGroup({
        line,
        parentKey: paletteBuilderParentKey,
      });
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }

    if (!cartItemRowToRemove) return;

    const paletteBuilderRowsToRemove = paletteBuilderParentKey
      ? this.refs.cartItemRows.filter((row) => row.dataset.paletteBuilderParentKey === paletteBuilderParentKey)
      : [];

    const rowsToRemove = [
      cartItemRowToRemove,
      ...paletteBuilderRowsToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ].filter((row, index, rows) => row && rows.indexOf(row) === index);

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Removes a palette builder parent and every component line in the same group.
   * @param {Object} config
   * @param {number} config.line
   * @param {string} config.parentKey
   */
  removePaletteBuilderGroup(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:palette-builder-group');

    this.#disableCartItems();

    const { line, parentKey } = config;
    const { cartTotal } = this.refs;
    const sectionsToUpdate = this.#getSectionsToUpdate();

    cartTotal?.shimmer();

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const updates = {};

        for (const item of cart.items || []) {
          if (item.properties?._palette_builder_parent_key === parentKey) {
            updates[item.key] = 0;
          }
        }

        if (Object.keys(updates).length === 0) {
          const body = JSON.stringify({
            line,
            quantity: 0,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          });

          return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        }

        const body = JSON.stringify({
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        return fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
      })
      .then((response) => response.text())
      .then((responseText) => {
        this.#handleCartMutationResponse(line, responseText);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const cartTransformReconciliation = this.#getCartTransformReconciliation(line, quantity);
    let isReloadingForCartTransform = false;

    if (cartTransformReconciliation.required) {
      this.#disableCheckoutForCartTransformPricing();
      this.#setCartTransformPricingMessage(
        line,
        'Updating your bundle price. Checkout will be available when it is ready.'
      );
    }

    const sectionsToUpdate = this.#getSectionsToUpdate();

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        if (cartTransformReconciliation.required) {
          const parsedResponseText = this.#parseCartMutationResponse(line, responseText);

          if (!parsedResponseText) {
            this.#enableCheckoutAfterCartTransformFailure();
            return;
          }

          const pricingSettled = await this.#waitForCartTransformPricing(
            cartTransformReconciliation.pricedBundleCountAfterMutation,
            cartTransformReconciliation
          );

          if (pricingSettled) {
            isReloadingForCartTransform = true;
            window.location.reload();
          } else {
            this.#setCartTransformPricingMessage(
              line,
              'We could not confirm the bundle price yet. Refresh this page, or remove and rebuild the bundle before checkout.'
            );
          }
          return;
        }

        this.#handleCartMutationResponse(line, responseText);
      })
      .catch((error) => {
        console.error(error);
        if (cartTransformReconciliation.required) {
          this.#setCartTransformPricingMessage(
            line,
            'We could not confirm the bundle update. Refresh this page before checkout.'
          );
        }
      })
      .finally(() => {
        if (!isReloadingForCartTransform) this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Shopify can return an intermediate, untransformed section after changing a
   * transformed bundle. A completed document request receives the transformed
   * price, so reconcile quantity changes while a transformed bundle survives.
   *
   * @param {number} line
   * @param {number} nextQuantity
   * @returns {{required: boolean, pricedBundleCountAfterMutation: number, fastBundleOnly: boolean, targetFastBundleId: string, expectedQuantity: number|null, expectedProviderLinePriceCents: number|null, expectedDisplayedItemCount: number|null}}
   */
  #getCartTransformReconciliation(line, nextQuantity) {
    const lineItemRow = this.#getCartItemRowByLine(line);
    const currentQuantity = Number.parseInt(lineItemRow?.dataset.cartLineQuantity ?? '', 10);
    const isPricedCartTransformBundle =
      lineItemRow?.dataset.cartTransformBundle === 'true' &&
      lineItemRow?.dataset.cartTransformPriced === 'true';
    const isFastBundleTarget = lineItemRow?.dataset.fastBundleCartLine === 'true';
    const fastBundleOnly = isFastBundleTarget && nextQuantity > 0;
    const targetFastBundleId = isFastBundleTarget
      ? String(lineItemRow?.dataset.fastBundleId || '')
      : '';
    const providerLinePriceCents = Number.parseInt(
      lineItemRow?.dataset.fastBundleProviderLinePriceCents ?? '',
      10
    );
    const expectedProviderLinePriceCents = isFastBundleTarget
      && Number.isSafeInteger(providerLinePriceCents)
      && Number.isSafeInteger(currentQuantity)
      && currentQuantity > 0
      && providerLinePriceCents % currentQuantity === 0
        ? (providerLinePriceCents / currentQuantity) * nextQuantity
        : null;
    const currentDisplayedItemCount = Number.parseInt(
      this.querySelector('[ref="cartItemCount"]')?.textContent ?? '',
      10
    );
    const expectedDisplayedItemCount = !isFastBundleTarget
      && Number.isSafeInteger(currentDisplayedItemCount)
      && Number.isSafeInteger(currentQuantity)
      && Number.isSafeInteger(nextQuantity)
        ? currentDisplayedItemCount - currentQuantity + nextQuantity
        : null;
    const pricedBundleCount = countPricedCartTransformRows(this.refs.cartItemRows, {
      fastBundleOnly,
    });
    const pricedBundleCountAfterMutation = Math.max(
      0,
      pricedBundleCount - (isPricedCartTransformBundle && nextQuantity === 0 ? 1 : 0)
    );

    return {
      required: shouldReconcileCartTransformMutation({
        isPricedCartTransformBundle,
        isFastBundleCartLine: isFastBundleTarget,
        currentQuantity,
        nextQuantity,
        pricedBundleCountAfterMutation,
      }),
      pricedBundleCountAfterMutation,
      fastBundleOnly,
      targetFastBundleId,
      expectedQuantity: isFastBundleTarget ? nextQuantity : null,
      expectedProviderLinePriceCents,
      expectedDisplayedItemCount,
    };
  }

  /**
   * Waits for a full-page render to show that Cart Transform pricing has
   * settled. Fast Bundle checks remain tied to the exact provider bundle,
   * quantity and price while generic transforms use row counts.
   *
   * @param {number} expectedPricedBundleCount
   * @param {{fastBundleOnly?: boolean, targetFastBundleId?: string, expectedQuantity?: number|null, expectedProviderLinePriceCents?: number|null, expectedDisplayedItemCount?: number|null, requiredFastBundleIds?: string[]}} [options]
   * @returns {Promise<boolean>}
   */
  async #waitForCartTransformPricing(
    expectedPricedBundleCount,
    {
      fastBundleOnly = false,
      targetFastBundleId = '',
      expectedQuantity = null,
      expectedProviderLinePriceCents = null,
      expectedDisplayedItemCount = null,
      requiredFastBundleIds = [],
    } = {}
  ) {
    const retryDelays = [0, 250, 500, 1000, 1500, 2500, 4000, 5000, 7500, 10000, 10000];
    const reconciliationDeadline = Date.now() + 45000;

    for (const retryDelay of retryDelays) {
      const remainingBeforeDelay = reconciliationDeadline - Date.now();
      if (remainingBeforeDelay <= 0) return false;

      if (retryDelay > 0) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, Math.min(retryDelay, remainingBeforeDelay))
        );
      }

      const remainingForRequest = reconciliationDeadline - Date.now();
      if (remainingForRequest <= 0) return false;

      const controller = new AbortController();
      const requestTimeout = window.setTimeout(
        () => controller.abort(),
        Math.min(3500, remainingForRequest)
      );

      try {
        const response = await fetch(window.location.href, {
          cache: 'no-store',
          headers: { Accept: 'text/html' },
          signal: controller.signal,
        });

        if (!response.ok) continue;

        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const matchingCartItems = Array.from(page.querySelectorAll('cart-items-component')).find(
          (component) => component.dataset.sectionId === this.sectionId
        );
        const probeSettled = isCartTransformProbeSettled(
          matchingCartItems?.querySelectorAll('[ref="cartItemRows[]"]'),
          {
            expectedPricedBundleCount,
            fastBundleOnly,
            targetFastBundleId,
            expectedQuantity,
            expectedProviderLinePriceCents,
            expectedDisplayedItemCount,
            renderedDisplayedItemCount: Number.parseInt(
              matchingCartItems?.querySelector('[ref="cartItemCount"]')?.textContent ?? '',
              10
            ),
            requiredFastBundleIds,
          }
        );

        if (probeSettled) {
          return true;
        }
      } catch {
        // A transient background-probe failure is retried within the deadline.
      } finally {
        window.clearTimeout(requestTimeout);
      }
    }

    return false;
  }

  /**
   * Recovers a Fast Bundle line when a buyer loads the cart while Shopify is
   * still returning its temporary catalogue price. The checkout remains
   * unavailable until the provider's BUNDLE allocation is present again.
   * On a fresh document that provider allocation is the price authority; the
   * theme deliberately has no duplicated or hard-coded offer amount to compare.
   * Same-session adds and later quantity edits perform exact price checks.
   */
  async #recoverUnpricedFastBundlePricing() {
    if (this.#isRecoveringCartTransformPricing) return;

    const cartItemRows = Array.from(this.refs.cartItemRows || []);
    const cartTransformStates = cartItemRows.map((row) => ({
      isFastBundleCartLine: row.dataset.fastBundleCartLine === 'true',
      isPricedCartTransformBundle: row.dataset.cartTransformPriced === 'true',
    }));
    const hasUnpricedFastBundle = shouldRecoverUnpricedFastBundlePricing(cartTransformStates);

    if (!hasUnpricedFastBundle) return;

    const fastBundleRows = cartItemRows.filter(
      (row) => row.dataset.fastBundleCartLine === 'true'
    );
    const requiredFastBundleIds = fastBundleRows.map(
      (row) => String(row.dataset.fastBundleId || '')
    );

    const affectedLine = Number.parseInt(fastBundleRows[0]?.dataset.line ?? '', 10);
    this.#isRecoveringCartTransformPricing = true;
    this.#disableCartItems();
    this.#disableCheckoutForCartTransformPricing();
    this.#setCartTransformPricingMessage(
      affectedLine,
      'Updating your bundle price. Checkout will be available when it is ready.'
    );

    const pricingSettled = await this.#waitForCartTransformPricing(
      fastBundleRows.length,
      { fastBundleOnly: true, requiredFastBundleIds }
    );

    if (pricingSettled) {
      window.location.reload();
      return;
    }

    this.#isRecoveringCartTransformPricing = false;
    this.#enableCartItems();
    this.#setCartTransformPricingMessage(
      affectedLine,
      'We could not confirm the bundle price yet. Refresh this page, or remove and rebuild the bundle before checkout.'
    );
  }

  /**
   * Prevents checkout from using an unconfirmed Cart Transform price.
   */
  #disableCheckoutForCartTransformPricing() {
    for (const checkoutControl of document.querySelectorAll(
      '[name="checkout"], shopify-accelerated-checkout-cart'
    )) {
      checkoutControl.setAttribute('aria-disabled', 'true');
      checkoutControl.setAttribute('data-cart-transform-pricing-pending', 'true');

      if (checkoutControl instanceof HTMLButtonElement) {
        checkoutControl.disabled = true;
      } else if (checkoutControl instanceof HTMLElement) {
        checkoutControl.inert = true;
      }
    }
  }

  /**
   * Restores checkout only after Shopify definitively rejects the cart change,
   * leaving the previously confirmed bundle untouched.
   */
  #enableCheckoutAfterCartTransformFailure() {
    for (const checkoutControl of document.querySelectorAll(
      '[data-cart-transform-pricing-pending="true"]'
    )) {
      checkoutControl.removeAttribute('aria-disabled');
      checkoutControl.removeAttribute('data-cart-transform-pricing-pending');

      if (checkoutControl instanceof HTMLButtonElement) {
        checkoutControl.disabled = false;
      } else if (checkoutControl instanceof HTMLElement) {
        checkoutControl.inert = false;
      }
    }
  }

  /**
   * Shows provider reconciliation progress beside the affected bundle line.
   *
   * @param {number} line
   * @param {string} message
   */
  #setCartTransformPricingMessage(line, message) {
    if (!Number.isFinite(line)) return;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (
      !(cartItemError instanceof HTMLElement) ||
      !(cartItemErrorContainer instanceof HTMLElement)
    ) {
      return;
    }

    cartItemError.textContent = message;
    cartItemErrorContainer.classList.remove('hidden');
  }

  /**
   * Parses a cart mutation and renders any Shopify error without mutating the
   * current cart section.
   *
   * @param {number} line
   * @param {string} responseText
   * @returns {Object | undefined}
   */
  #parseCartMutationResponse(line, responseText) {
    const parsedResponseText = JSON.parse(responseText);

    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    return parsedResponseText;
  }

  /**
   * Handles a cart mutation response that includes rendered sections.
   * @param {number} line
   * @param {string} responseText
   */
  #handleCartMutationResponse(line, responseText) {
    const parsedResponseText = this.#parseCartMutationResponse(line, responseText);

    if (!parsedResponseText) return;

    const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');

    // Grab the new cart item count from a hidden element
    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

    // Update data-cart-quantity for all matching variants
    this.#updateQuantitySelectors(parsedResponseText);

    this.dispatchEvent(
      new CartUpdateEvent(parsedResponseText, this.sectionId, {
        itemCount: newCartItemCount,
        source: 'cart-items-component',
        sections: parsedResponseText.sections,
      })
    );

    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Gets cart sections that need to be re-rendered after a mutation.
   * @returns {Set<string>}
   */
  #getSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    return sectionsToUpdate;
  }

  /**
   * Finds the rendered cart row for a Shopify cart line number.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRowByLine(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === String(line));
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const lineItemRow = this.#getCartItemRowByLine(line);
    const quantitySelector = lineItemRow?.querySelector('quantity-selector-component') ?? this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
